import useGetAllClientIdentityApisInfinite from '@/hooks/query/useGetAllClientIdentityApisInfinite'
import { Label } from '@radix-ui/react-dropdown-menu'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { ClientIdentityApiCredsDto } from '@/gen/publicApi'

import Breadcrumbs from '@/containers/breadcrumbs'
import { UnexpectedDataStateError } from '@/containers/unexpectedDataStateError'

import { Button } from '@/components/button'
import { Card } from '@/components/card'
import Content from '@/components/content'
import CopyToClipboard from '@/components/copyIcon'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/dialog'
import { Input } from '@/components/input'
import LoadMoreButton from '@/components/loadMoreButton'
import PageHeader from '@/components/pageHeader'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table'

import { useSetBreadcrumbs } from '@/state/breadcrumbs'

import SettingsNavigation from '../-components/settingsNavigation'

import CreateApiKeyForm from './-components/createApiKeyForm'
import DeleteApiKey from './-components/deleteApiKey'
import EmptyApiKeys from './-components/empty'

export const Route = createFileRoute('/_authed/$client_code/settings/apikeys/')({
    component: ApiKeys,
})

function ApiKeys() {
    const [createSheetOpen, setCreateSheetOpen] = useState(false)
    const [createdApiKeyDetails, setCreatedApiKeyDetails] = useState<ClientIdentityApiCredsDto | null>(null)
    const { client_code } = Route.useParams()
    // TODO - add pagination in here
    const {
        data: apiKeys,
        refetch,
        fetchNextPage,
        hasNextPage,
        isLoading,
    } = useGetAllClientIdentityApisInfinite(client_code)
    useSetBreadcrumbs([
        {
            to: '/$client_code/settings',
            text: 'Settings',
            params: { client_code },
        },
        'seperator',
        {
            to: '/$client_code/apikeys',
            text: 'Api Keys',
            params: { client_code },
        },
    ])

    if (!apiKeys) {
        return <UnexpectedDataStateError />
    }

    const onCreatedApiKeyDetails = (apiKey: ClientIdentityApiCredsDto) => {
        setCreatedApiKeyDetails(apiKey)
        setCreateSheetOpen(false)
        refetch()
    }

    return (
        <>
            <Breadcrumbs />
            <Content>
                <PageHeader
                    title="Api Keys"
                    controls={
                        <Sheet open={createSheetOpen} onOpenChange={setCreateSheetOpen}>
                            <SheetTrigger asChild>
                                <Button variant={'outline'} className="mb-2" onClick={() => setCreateSheetOpen(true)}>
                                    Create
                                </Button>
                            </SheetTrigger>
                            <SheetContent>
                                <SheetHeader>
                                    <SheetTitle>Create API Key</SheetTitle>
                                </SheetHeader>
                                <CreateApiKeyForm onCreate={onCreatedApiKeyDetails} />
                            </SheetContent>
                        </Sheet>
                    }
                />
                <SettingsNavigation active="apiKeys" />

                <Card hideOverflow>
                    {apiKeys && apiKeys.length > 0 ? (
                        <>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Username</TableHead>
                                        <TableHead />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {apiKeys.map(apiKey => (
                                        <TableRow key={apiKey.id}>
                                            <TableCell>{apiKey.name}</TableCell>
                                            <TableCell>{apiKey.identifier}</TableCell>
                                            <TableCell>
                                                <DeleteApiKey clientCode={client_code} apiKey={apiKey} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <div className="flex justify-center">
                                <LoadMoreButton
                                    className="my-4"
                                    fetchNextPage={fetchNextPage}
                                    isLoading={isLoading}
                                    hasNextPage={hasNextPage}
                                />
                            </div>
                        </>
                    ) : (
                        <EmptyApiKeys onClick={() => setCreateSheetOpen(true)} />
                    )}
                </Card>
                <Dialog
                    open={!!createdApiKeyDetails}
                    onOpenChange={() => {
                        setCreatedApiKeyDetails(null)
                    }}
                >
                    <DialogContent>
                        <DialogTitle>API Key created</DialogTitle>
                        <DialogDescription>
                            Your API key has been created. Copy the password and keep it safe, this is the only time we
                            will show it to you.
                        </DialogDescription>
                        <div>
                            <Label className="mt-4">Username</Label>
                            <div className="flex space-x-4">
                                <Input className="flex-grow" value={createdApiKeyDetails?.identifier} disabled />
                                <CopyToClipboard value={createdApiKeyDetails?.identifier || ''} name="username" />
                            </div>
                        </div>
                        <div>
                            <Label>Password</Label>
                            <div className="flex space-x-4">
                                <Input className="flex-grow" value={createdApiKeyDetails?.password} disabled />
                                <CopyToClipboard value={createdApiKeyDetails?.password || ''} name="password" />
                            </div>
                        </div>
                        <Button variant="outline" onClick={() => setCreatedApiKeyDetails(null)}>
                            Close
                        </Button>
                    </DialogContent>
                </Dialog>
            </Content>
        </>
    )
}
